import { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Check, ExternalLink } from 'lucide-react';
import { createPayPalOrder, capturePayPalOrder } from '@/lib/api';
import { toast } from 'sonner';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export const PayPalBuy = ({ book, selectedFormat, onSelectFormat }) => {
  const formats = book.formats || [];
  const [purchased, setPurchased] = useState(false);

  const activeFormat = formats.find((f) => f.name === selectedFormat) || formats[0];

  if (formats.length === 0) return null;

  return (
    <div className="space-y-6" data-testid="paypal-buy">
      {/* Format selection */}
      <div>
        <p className="text-sm text-white/40 mb-3 uppercase tracking-widest">Choose Format</p>
        <div className="flex flex-wrap gap-3">
          {formats.map((fmt) => (
            <button
              key={fmt.name}
              onClick={() => {
                onSelectFormat(fmt.name);
                setPurchased(false);
              }}
              className={`flex items-center justify-between gap-4 px-5 py-3 border transition-colors min-w-[160px] ${
                selectedFormat === fmt.name
                  ? 'border-gold text-white bg-gold/5'
                  : 'border-white/15 text-white/60 hover:border-white/40'
              }`}
              data-testid={`format-option-${fmt.name.toLowerCase()}`}
            >
              <span className="text-sm">{fmt.name}</span>
              <span className="mono text-gold text-sm">${fmt.price.toFixed(2)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Price display */}
      <p className="mono text-2xl text-gold" data-testid="paypal-active-price">
        ${activeFormat?.price.toFixed(2)}
        <span className="text-white/40 text-sm ml-2">/ {activeFormat?.name}</span>
      </p>

      {activeFormat?.ingram_url && (
        <a
          href={activeFormat.ingram_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 hover:border-gold text-white/70 hover:text-gold text-sm transition-colors"
          data-testid="ingramspark-link"
        >
          Buy on IngramSpark
          <ExternalLink size={13} />
        </a>
      )}

      {purchased ? (
        <div
          className="flex items-center gap-3 px-5 py-4 border border-gold/40 bg-gold/5 text-gold"
          data-testid="paypal-success"
        >
          <Check size={20} />
          <span className="text-sm">Payment complete — thank you for your order!</span>
        </div>
      ) : PAYPAL_CLIENT_ID ? (
        <div className="max-w-sm" data-testid="paypal-buttons-container">
          <PayPalScriptProvider
            options={{ clientId: PAYPAL_CLIENT_ID, currency: 'USD', intent: 'capture' }}
          >
            <PayPalButtons
              key={selectedFormat}
              style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'buynow' }}
              createOrder={async () => {
                const order = await createPayPalOrder(book.id, selectedFormat);
                return order.id;
              }}
              onApprove={async (data) => {
                try {
                  const result = await capturePayPalOrder(data.orderID);
                  if (result.status === 'COMPLETED') {
                    setPurchased(true);
                    toast.success('Payment successful!');
                  } else {
                    toast.error('Payment could not be completed.');
                  }
                } catch (e) {
                  toast.error('Failed to capture payment.');
                }
              }}
              onError={() => toast.error('PayPal encountered an error. Please try again.')}
            />
          </PayPalScriptProvider>
        </div>
      ) : (
        <div
          className="px-5 py-4 border border-white/15 text-white/50 text-sm max-w-sm"
          data-testid="paypal-not-configured"
        >
          PayPal checkout is being configured. Please check back shortly.
        </div>
      )}
    </div>
  );
};

export default PayPalBuy;
